import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { Survey } from '@app/models/performance/survey.model';
import { AnalyticService, NotifyService } from '@app/services';
import { ColorService } from '@app/services/color.service';
import { Translatable } from '@app/types/translatable.type';
import { BarControllerDatasetOptions, ChartData } from 'chart.js';
import { Options } from 'chartjs-plugin-datalabels/types/options';
import { ChartObject } from '../../interface/chart-data.interface';
import { CHART_OPTIONS } from '../../meta/survey-chart-options';
import { SURVEY_TYPES } from '../../meta/survey-types';
import { getSurveyResultsTitleTooltip } from '../../meta/tooltip-meta';
import { SurveyMenuService, SurveyService } from '../../services';
@Component({
    templateUrl: './aggregated-data-survey.template.html',
    styleUrls: ['./aggregated-data-survey.style.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AggregatedDataSurveyComponent implements OnInit {
    survey: Survey = this.route.snapshot.data.models.get('survey');
    charts: ChartObject[] = [];
    booleanBarChartData!: ChartData;
    chartOptions = CHART_OPTIONS;
    isPrinting = false;

    routeToRedirectTo = '/surveys/center';
    surveyResultsTitleTooltip!: Translatable;

    barChartScaleValue = 5; // Scales desired axes to have 5 ticks based on the maximum value. i.e. 3 / 5 = 0.6 increments per tick.

    constructor(
        public tabMenuService: SurveyMenuService,
        private surveyService: SurveyService,
        private route: ActivatedRoute,
        private notify: NotifyService,
        private analyticsService: AnalyticService,
        private colorService: ColorService
    ) {}

    async ngOnInit(): Promise<void> {
        try {
            this.charts = await this.surveyService.getSurvey(this.survey.id);
        } catch (err) {
            this.notify.error(err);
        }

        // charts[0] can be empty if user assigned a template with blank questions.
        this.surveyResultsTitleTooltip = getSurveyResultsTitleTooltip(
            this.charts[0]?.totalAnswers,
            this.charts[0]?.totalParticipants
        );

        this.setChartsData(this.charts);
    }

    print(): void {
        this.isPrinting = true;
        setTimeout(() => {
            const prtContent = $('.ui.form.fields.printable')[0];
            const prtContentStyles = $('style');
            const winPrint = window.open('', '', 'left=50,top=50,width=800,height=900,toolbar=0,scrollbars=0,status=0');
            winPrint.document.write(`<html><head></head><body></body></html>`);
            winPrint.document.getElementsByTagName('body')[0].innerHTML += prtContent.innerHTML;
            winPrint.document.getElementsByTagName('head')[0].innerHTML +=
                '<link rel="stylesheet" type="text/css" href="/assets/semantic/semantic.min.css">';
            winPrint.document.getElementsByTagName('head')[0].innerHTML += `<style>
                    .chart-card {
                        margin-bottom: 24px;
                        padding: 24px;
                    }
                </style>`;
            prtContentStyles.each(function (_i: number, o: any): void {
                winPrint.document.getElementsByTagName('body')[0].innerHTML += '<style>' + $(o).html() + '</style>';
            });

            winPrint.document.close();
            winPrint.focus();

            this.analyticsService.trackEvent(
                AnalyticEvents.SurveysIndividualSurveyClickViewResultsAggregatedDataClickPrint
            );

            setTimeout(() => {
                winPrint.print();
                winPrint.close();
                this.isPrinting = false;
            }, 1500);
        }, 100);
    }

    setChartsData(charts: ChartObject[]): void {
        charts.forEach((chart: ChartObject) => {
            switch (chart.type) {
                case SURVEY_TYPES.SCALE:
                    chart.chartData = this.mapToChart(
                        chart,
                        {
                            backgroundColor: [
                                this.colorService.getColorValue('error', 800),
                                this.colorService.getColorValue('highlight', 500),
                                this.colorService.getColorValue('warning', 500),
                                this.colorService.getColorValue('success', 400),
                                this.colorService.getColorValue('success', 700),
                            ],
                        },
                        {
                            color: this.colorService.getColorValue('grey', 900),
                            anchor: 'end',
                            align: 'top',
                            formatter: function (value): string {
                                if (+value === 0) {
                                    return '';
                                }

                                return Math.round((value / chart.totalAnswers) * 100) + '%';
                            },
                            font: {
                                weight: 'bold',
                                size: 16,
                            },
                        }
                    );
                    chart.chartOptions = this.chartOptions[chart.type];
                    if (!chart.chartOptions.scales) {
                        chart.chartOptions.scales = {};
                    }
                    chart.chartOptions.scales.y = {
                        grid: { display: false },
                        beginAtZero: true,
                        max: chart.totalAnswers,
                        ticks: {
                            color: this.colorService.getColorValue('grey', 900),
                            font: {
                                family: 'Tenon',
                                size: 13,
                            },
                            stepSize: chart.totalAnswers / this.barChartScaleValue,
                            callback: function (label: number): string {
                                return Math.round((label / chart.totalAnswers) * 100) + '%';
                            },
                        },
                    };
                    chart.chartOptions.scales.x = {
                        ticks: {
                            color: this.colorService.getColorValue('grey', 900),
                        },
                        grid: { display: false },
                    };
                    chart.chartMetaData = {
                        description: '',
                        chartType: 'bar',
                        showLegend: false,
                        isStacked: false,
                        chartData: chart.chartData,
                    };

                    break;

                case SURVEY_TYPES.MULTIPLE:
                    chart.chartData = this.mapToChart(
                        chart,
                        {
                            categoryPercentage: 0.8,
                            barPercentage: 1,
                            backgroundColor: this.colorService.getColorValue('success', 700),
                        },
                        {
                            color: this.colorService.getColorValue('grey', 900),
                            anchor: 'end',
                            align: 'right',
                            formatter: function (value): string {
                                if (+value === 0) {
                                    return '';
                                }

                                return Math.round((value / chart.totalAnswers) * 100) + '%';
                            },
                            font: {
                                weight: 'bold',
                                size: 16,
                            },
                        }
                    );

                    chart.chartOptions = this.chartOptions[chart.type];
                    if (!chart.chartOptions.scales) {
                        chart.chartOptions.scales = {};
                    }
                    chart.chartOptions.indexAxis = 'y';
                    chart.chartOptions.scales.x = {
                        grid: { display: false },
                        beginAtZero: true,
                        max: chart.totalAnswers,
                        ticks: {
                            color: this.colorService.getColorValue('grey', 900),
                            font: {
                                family: 'Tenon',
                                size: 15,
                            },
                            stepSize: chart.totalAnswers / this.barChartScaleValue,
                            callback: function (label: number): string {
                                return (label / chart.totalAnswers) * 100 + '%';
                            },
                        },
                    };
                    chart.chartOptions.scales.y = {
                        grid: { display: false },
                        ticks: {
                            color: this.colorService.getColorValue('grey', 900),
                            font: {
                                family: 'Tenon',
                                size: 13,
                            },
                        },
                    };

                    chart.chartMetaData = {
                        description: '',
                        chartType: 'bar',
                        showLegend: false,
                        isStacked: false,
                        chartData: chart.chartData,
                    };

                    break;

                case SURVEY_TYPES.BOOLEAN:
                    chart.chartData = this.mapToChart(
                        chart,
                        {
                            backgroundColor: [
                                this.colorService.getColorValue('error', 800),
                                this.colorService.getColorValue('warning', 600),
                                this.colorService.getColorValue('success', 700),
                            ],
                        },
                        {
                            color: this.colorService.getColorValue('grey', 900),
                            anchor: 'end',
                            align: 'end',
                            formatter: function (value, context): string {
                                // If both values are 0 and then you we need to escape
                                if (!value && !chart?.totalAnswers) {
                                    return 'No data';
                                }

                                return (
                                    (context.chart.data.labels[context.dataIndex] === 'True' ? 'Yes' : 'No') +
                                    '  ' +
                                    Math.round((value / chart.totalAnswers) * 100) +
                                    '%'
                                );
                            },
                            font: {
                                weight: 'bold',
                                size: 16,
                            },
                        }
                    );

                    chart.chartOptions = this.chartOptions[chart.type];

                    chart.chartMetaData = {
                        description: '',
                        chartType: 'pie',
                        showLegend: false,
                        isStacked: false,
                        chartData: chart.chartData,
                    };

                    break;

                case SURVEY_TYPES.RATING:
                    chart.chartData = this.mapToChart(
                        chart,
                        { backgroundColor: this.colorService.getColorValue('success', 700) },
                        {
                            color: this.colorService.getColorValue('grey', 900),
                            anchor: 'end',
                            align: 'top',
                            formatter: function (value): string {
                                if (+value === 0) {
                                    return '';
                                }

                                return Math.round((value / chart.totalAnswers) * 100) + '%';
                            },
                            font: {
                                weight: 'bold',
                                size: 16,
                            },
                        }
                    );
                    chart.chartOptions = this.chartOptions[chart.type];
                    if (!chart.chartOptions.scales) {
                        chart.chartOptions.scales = {};
                    }
                    chart.chartOptions.scales.y = {
                        grid: { display: false },
                        beginAtZero: true,
                        max: chart.totalAnswers ?? 0,
                        ticks: {
                            color: this.colorService.getColorValue('grey', 900),
                            font: {
                                family: 'Tenon',
                                size: 13,
                            },
                            stepSize: chart.totalAnswers / this.barChartScaleValue,
                            callback: function (label: number): string {
                                return (label / chart.totalAnswers) * 100 + '%';
                            },
                        },
                    };
                    chart.chartOptions.scales.x = {
                        grid: { display: false },
                        ticks: {
                            color: this.colorService.getColorValue('grey', 900),
                        },
                    };
                    chart.chartMetaData = {
                        description: '',
                        chartType: 'bar',
                        showLegend: false,
                        isStacked: false,
                        chartData: chart.chartData,
                    };

                    break;
            }
        });
    }

    mapToChart(
        data: ChartObject,
        chartDatasetOptions: Partial<BarControllerDatasetOptions>,
        datalabels: Options
    ): ChartData {
        return {
            labels: data.labels,
            datasets: [
                {
                    data: data.answers,
                    datalabels,
                    ...chartDatasetOptions,
                },
            ],
        };
    }
}
